import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { formatDate } from '../lib/utils';
import {
  Trash2,
  Download,
  AlertTriangle,
  Loader2,
  User,
  FileText,
  Shield,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';

const MyAccount = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [submissionCount, setSubmissionCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const allRes = await api.get('/submissions/list?skip=0&limit=100');
        setSubmissionCount(allRes.data.length);
      } catch (err) {
        console.error('Failed to fetch submissions count');
      } finally {
        setLoading(false);
      }
    };
    fetchCount();
  }, []);

  const handleDeleteSubmission = async (submissionId, title) => {
    if (!window.confirm(`Delete submission "${title}"? This cannot be undone.`)) return;

    setActionLoading(`del-${submissionId}`);
    try {
      await api.delete(`/maintenance/my-submission/${submissionId}`);
      toast.success('Submission deleted');
      // Update local state directly instead of re-fetching
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
      setSubmissionCount(prev => prev - 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete submission');
    } finally {
      setActionLoading('');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete ALL ${submissionCount} submissions? This action is permanent and cannot be undone.`)) return;
    if (!window.confirm('Are you absolutely sure? All your documents and reports will be permanently removed.')) return;

    setActionLoading('delete-all');
    try {
      const res = await api.delete('/maintenance/my-submissions');
      toast.success(res.data.message);
      setSubmissionCount(0);
      setSubmissions([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete submissions');
    } finally {
      setActionLoading('');
    }
  };

  const handleExportData = async () => {
    setActionLoading('export');
    try {
      const res = await api.get('/maintenance/my-data-export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `my_data_export_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (err) {
      toast.error('Failed to export data');
    } finally {
      setActionLoading('');
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Delete your account? All your data, submissions, and reports will be permanently removed.')) return;
    if (!window.confirm('THIS IS YOUR LAST CHANCE. Your account and all data will be permanently deleted. Continue?')) return;

    setActionLoading('delete-account');
    try {
      await api.delete('/maintenance/my-account');
      toast.success('Account deleted successfully');
      logout();
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
    } finally {
      setActionLoading('');
    }
  };

  const [submissions, setSubmissions] = useState([]);
  useEffect(() => {
    const fetchSubs = async () => {
      try {
        const res = await api.get('/submissions/list?skip=0&limit=100');
        setSubmissions(res.data);
      } catch (err) {
        console.error('Failed to fetch submissions');
      }
    };
    fetchSubs();
  }, []);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="my-account-page">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-bold text-foreground">My Account</h1>
          <p className="text-muted-foreground mt-1">Manage your data, submissions, and account settings</p>
        </div>

        {/* Profile Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Full Name</p>
                <p className="font-medium text-foreground">{user?.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Student ID</p>
                <p className="font-medium text-foreground">{user?.student_id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium text-foreground">{user?.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Account Status</p>
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">Active</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Submissions</p>
                <p className="font-medium text-foreground">{loading ? '...' : submissionCount}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Member Since</p>
                <p className="font-medium text-foreground">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export My Data */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-500" />
              Export My Data
            </CardTitle>
            <CardDescription>
              Download all your data including profile, submissions, and scan results as a JSON file
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={handleExportData}
              disabled={actionLoading === 'export'}
              data-testid="export-my-data-btn"
            >
              {actionLoading === 'export' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download My Data
            </Button>
          </CardContent>
        </Card>

        {/* My Submissions - Delete Individual */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Manage Submissions
            </CardTitle>
            <CardDescription>Delete individual submissions or all at once</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submissions.length === 0 ? (
              <p className="text-muted-foreground text-sm">No submissions to manage</p>
            ) : (
              <>
                <div className="max-h-80 overflow-y-auto divide-y divide-border rounded-sm border border-border">
                  {submissions.map((sub) => (
                    <div key={sub.id} className="flex items-center justify-between p-3 hover:bg-muted/30" data-testid={`manage-submission-${sub.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground text-sm truncate">{sub.title}</p>
                        <p className="text-xs text-muted-foreground">{sub.file_name} &middot; {formatDate(sub.created_at)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-2 flex-shrink-0"
                        onClick={() => handleDeleteSubmission(sub.id, sub.title)}
                        disabled={actionLoading === `del-${sub.id}`}
                        data-testid={`delete-submission-${sub.id}`}
                      >
                        {actionLoading === `del-${sub.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    </div>
                  ))}
                </div>

                <Button
                  variant="destructive"
                  onClick={handleDeleteAll}
                  disabled={actionLoading === 'delete-all' || submissionCount === 0}
                  data-testid="delete-all-submissions-btn"
                >
                  {actionLoading === 'delete-all' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete All Submissions ({submissionCount})
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-lg text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Permanently delete your account and all associated data. This action cannot be reversed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                Deleting your account will permanently remove your profile, all submissions, scan results, and uploaded files. You will not be able to recover any data.
              </AlertDescription>
            </Alert>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={actionLoading === 'delete-account'}
              data-testid="delete-account-btn"
            >
              {actionLoading === 'delete-account' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete My Account Permanently
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MyAccount;
